import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { useOscarClient } from '~/composables/useOscarClient'
import {
  findAddressById,
  getAddressCheckoutFormat,
  getAddressDisplay,
} from '~/utils/address'
import { useBasketStore } from './basket'

export const useCheckoutStore = defineStore('checkout', () => {
  const shippingSelected = ref(null)
  const billingSelected = ref(null)
  const deliverySlot = ref(null)
  const arrival = ref(null)
  const deliveryDate = ref(null)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const selectedSlot = ref(tomorrow)
  const arrivalVal = ref(null)
  const drop = ref(null)
  const shipping = ref(null)
  const paymentType = ref(null)
  const shippingSlots = ref(null)
  const shippingMessages = ref([])
  const paymentUrl = ref('')
  const paymentOrderNumber = ref('')
  const paymentHash = ref('')
  const slotInfo = ref({})
  const basketStore = useBasketStore()
  const route = useRoute()
  const { t, locale } = useI18n()

  const salutationMap = computed(() => {
    return {
      mr: t('account.registration.personal.salutation.mr'),
      ms: t('account.registration.personal.salutation.ms'),
    }
  })

  const shippingAddress = reactive({
    first_name: '',
    last_name: '',
    street: '',
    house_number: '',
    postcode: '',
    city: '',
    company: '',
    country: '',
    additional: '',
    title: '',
    phone: '',
  })
  const billingAddress = reactive({
    first_name: '',
    last_name: '',
    street: '',
    house_number: '',
    postcode: '',
    city: '',
    company: '',
    country: '',
    additional: '',
    title: '',
    phone: '',
  })

  const shippingFormCompleted = computed(() => {
    return (
      (!!shipping.value && !!selectedSlot.value && !!arrivalVal.value)
      || (!!selectedSlot.value && shipping.value === 2)
      || (!!shipping.value
        && !!selectedSlot.value
        && basketStore?.basket?.only_vouchers)
    )
  })

  const deliveryDateString = computed(() => {
    if (!deliveryDate.value)
      return ''
    return deliveryDate.value.toISOString().split('T')[0]
  })

  const delivery = computed(() => {
    if (!deliveryDate.value)
      return {}
    return {
      day: `${deliveryDate.value.toLocaleDateString('de-CH', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`,
      time:
                arrivalVal.value === 'pickup'
                || basketStore.basket.only_vouchers
                  ? ''
                  : `${arrival.value.label} (${arrival.value.time_from} - ${arrival.value.time_to} Uhr)`,
      pickup: arrivalVal.value === 'pickup',
    }
  })

  const differingInvoiceAddress = ref(false)

  const shippingAddressDisplay = computed(() => {
    return getAddressDisplay(shippingAddress, salutationMap.value)
  })

  const invoiceAddressDisplay = computed(() => {
    return differingInvoiceAddress.value && billingAddress.last_name
      ? getAddressDisplay(billingAddress, salutationMap.value)
      : getAddressDisplay(shippingAddress, salutationMap.value)
  })

  const addresses = ref([])

  function applySelectedShippingAddress() {
    if (!shippingSelected.value) {
      return
    }

    const selected = findAddressById(addresses.value, shippingSelected.value)
    if (!selected) {
      return
    }

    for (const [key, addressValue] of Object.entries(selected)) {
      shippingAddress[key] = addressValue
    }
  }

  const getUserAddresses = async () => {
    const oscar = useOscarClient(locale.value)
    addresses.value = (await oscar.getUserAddresses()).data
    addresses.value.forEach((address) => {
      if (address.is_default_for_billing && !billingSelected.value) {
        billingSelected.value = address.id
      }
      if (address.is_default_for_shipping && !shippingSelected.value) {
        shippingSelected.value = address.id
      }
    })
    if (!shippingSelected.value && addresses.value.length > 0) {
      shippingSelected.value = addresses.value[0].id
    }

    applySelectedShippingAddress()
  }

  function addressComplete(address, email) {
    return (
      address.first_name
      && address.last_name
      && (email ? address.email : true)
      && address.phone
      && address.street
      && address.house_number
      && address.city
      && address.postcode
      && address.country
    )
  }

  const addressFormComplete = computed(() => {
    return (
      (addressComplete(shippingAddress, true)
        && !differingInvoiceAddress.value)
      || (addressComplete(shippingAddress, true)
        && addressComplete(billingAddress, false))
    )
  })

  const addressesDisplay = computed(() => {
    return addresses.value.map(address =>
      getAddressDisplay(address, salutationMap.value),
    )
  })

  watch(shippingSelected, () => {
    applySelectedShippingAddress()
  })

  function updateBillingAddress(empty) {
    const selected = findAddressById(addresses.value, billingSelected.value)
    if (!selected)
      return
    for (const [key, value] of Object.entries(selected)) {
      billingAddress[key] = empty ? null : value
    }
  }

  const checkoutShippingAddress = computed(() => {
    return getAddressCheckoutFormat(shippingAddress)
  })

  const checkoutBillingAddress = computed(() => {
    return getAddressCheckoutFormat(billingAddress)
  })

  watch(billingSelected, () => {
    if (differingInvoiceAddress.value) {
      updateBillingAddress()
    }
  })
  watch(differingInvoiceAddress, (value) => {
    if (value) {
      updateBillingAddress()
    }
    else {
      updateBillingAddress(true)
    }
  })

  const isNextDayDelivery = computed(() => {
    return (
      deliveryDate.value
      && deliveryDate.value.toDateString() === tomorrow.toDateString()
    )
  })

  const isCheckoutPage = computed(() => {
    if (route?.name) {
      return route.name.startsWith('checkout')
    }
    return false
  })

  return {
    shippingAddress,
    billingAddress,
    addresses,
    getUserAddresses,
    addressesDisplay,
    differingInvoiceAddress,
    addressFormComplete,
    shippingAddressDisplay,
    invoiceAddressDisplay,
    delivery,
    shippingSelected,
    billingSelected,
    deliverySlot,
    deliveryDate,
    arrival,
    selectedSlot,
    arrivalVal,
    drop,
    shipping,
    paymentType,
    shippingFormCompleted,
    shippingSlots,
    paymentUrl,
    paymentOrderNumber,
    paymentHash,
    slotInfo,
    deliveryDateString,
    checkoutShippingAddress,
    checkoutBillingAddress,
    salutationMap,
    isNextDayDelivery,
    shippingMessages,
    isCheckoutPage,
  }
})
